import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import Calculator from "@/components/Calculator";
import ContactForm from "@/components/ContactForm";
import { images, isConceptVisual } from "@/content/images";
import { useLanguage } from "@/context/LanguageContext";

export default function Brands() {
  const { t } = useLanguage();

  return (
    <div className="bg-[#faf8f5] text-slate-900 antialiased min-h-screen">
      <Seo page="brands" />
      <Header />

      {/* Intro with café slot */}
      <section className="hero-grid-pat py-20 border-b spec-border" data-testid="brands-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <span className="text-xs font-mono-spec text-gold-700 font-semibold tracking-[0.18em] uppercase">{t.brandsPage.eyebrow}</span>
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-slate-950 mt-3 leading-[1.1]">
              {t.brandsPage.title}
            </h1>
            <p className="text-lg text-slate-600 mt-4 max-w-2xl leading-relaxed">{t.brandsPage.sub}</p>
            <Link
              to="/#contact"
              data-testid="brands-hero-cta"
              className="inline-flex items-center justify-center mt-8 px-6 py-3.5 bg-slate-950 text-white text-sm font-semibold rounded-sm hover:bg-gold-800 transition-colors shadow-md"
            >
              {t.brandsPage.cta}
            </Link>
          </div>
          <div className="lg:col-span-5">
            <div className="bg-white border spec-border p-3 shadow-md rounded-sm">
              <div className="aspect-[4/3] rounded-sm overflow-hidden">
                <img
                  src={images.cafe}
                  alt={t.brandsPage.imageAlt}
                  data-testid="brands-image"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <p className="mt-3 text-[11px] text-slate-500 font-mono-spec px-1 pb-1">
                {isConceptVisual ? t.hero.imageCaptionConcept : t.brandsPage.imageCaption}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why */}
      <section className="py-20 bg-white border-b spec-border" data-testid="brands-why">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl font-bold tracking-tight text-slate-950 mb-12">{t.brandsPage.whyTitle}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {t.brandsPage.why.map((w, i) => (
              <div key={w.t} className="p-6 bg-slate-50 border spec-border rounded-sm">
                <span className="font-mono-spec text-xs text-gold-700 font-bold">0{i + 1}</span>
                <h3 className="text-base font-bold text-slate-950 mt-2">{w.t}</h3>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{w.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use cases */}
      <section className="py-20 bg-slate-100/60 border-b spec-border" data-testid="brands-usecases">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl font-bold tracking-tight text-slate-950 mb-4">{t.brandsPage.usecasesTitle}</h2>
          <p className="text-xs font-mono-spec text-slate-500 mb-10">{t.brandsPage.usecasesNote}</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {t.brandsPage.usecases.map((u) => (
              <div key={u.t} className="p-6 bg-white border spec-border rounded-sm">
                <h3 className="text-base font-bold text-slate-950">{u.t}</h3>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{u.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Measurement */}
      <section className="py-20 bg-white border-b spec-border" data-testid="brands-measurement">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl font-bold tracking-tight text-slate-950 mb-4">{t.brandsPage.measurementTitle}</h2>
          <p className="text-xs font-mono-spec text-slate-500 mb-10">{t.brandsPage.measurementNote}</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {t.brandsPage.measurement.map((m) => (
              <div key={m.t} className="p-5 border spec-border rounded-sm">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="font-mono-spec text-sm font-bold text-slate-950 uppercase tracking-wide">{m.t}</h3>
                  <span
                    className={`text-[10px] font-mono-spec px-2 py-0.5 rounded-sm shrink-0 ${
                      m.status === "Implementiert" || m.status === "Implemented"
                        ? "bg-emerald-50 border border-emerald-200 text-emerald-800"
                        : "bg-slate-100 border spec-border text-slate-600"
                    }`}
                  >
                    {m.status}
                  </span>
                </div>
                <p className="text-sm text-slate-600 mt-2">{m.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Calculator />
      <ContactForm />
      <Footer />
    </div>
  );
}
